import React from "react";
import { Button } from "@mui/material";
import toast from "react-hot-toast";
import * as api from "../../Api/index";
import CodeInput from "../../Components/TwoFactor/CodeInput";
import BackupCodes from "../../Components/TwoFactor/BackupCodes";
import "../../Components/TwoFactor/TwoFactor.css";

const errorMessage = (error) =>
  error?.response?.data?.message || "Could not reach the server";

// After a correct password: ask for the authenticator code ("verify"), or set
// the app up first when it is required and the user has none ("setup").
// onDone gets the login answer; onCancel goes back to the password form.
export default function TwoFactorStep({ step, tempToken, onDone, onCancel }) {
  const [code, setCode] = React.useState("");
  const [useBackup, setUseBackup] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [setup, setSetup] = React.useState(null);
  const [backupCodes, setBackupCodes] = React.useState(null);
  const [loginAnswer, setLoginAnswer] = React.useState(null);

  React.useEffect(() => {
    if (step !== "setup") return;
    api
      .twoFactorLoginSetup({ tempToken })
      .then(({ data }) => setSetup(data))
      .catch((error) => {
        toast.error(errorMessage(error));
        onCancel();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, tempToken]);

  const submit = async (event) => {
    event.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      if (step === "verify") {
        const { data } = await api.twoFactorLoginVerify({ tempToken, code });
        onDone(data);
      } else {
        const { data } = await api.twoFactorLoginSetupConfirm({
          tempToken,
          code,
        });
        const { backupCodes: codes, ...answer } = data;
        setLoginAnswer(answer);
        setBackupCodes(codes);
      }
    } catch (error) {
      toast.error(errorMessage(error));
      setCode("");
      // the half-way token ran out: start again from the password
      if (/expired/i.test(errorMessage(error))) onCancel();
    } finally {
      setBusy(false);
    }
  };

  if (backupCodes) {
    return (
      <div className="tf-login">
        <h3 className="tf-heading">Authenticator app is set up</h3>
        <BackupCodes
          codes={backupCodes}
          onDone={() => onDone(loginAnswer)}
          doneLabel="Continue to CBX"
        />
      </div>
    );
  }

  return (
    <form className="tf-login" onSubmit={submit}>
      {step === "verify" ? (
        <>
          <h3 className="tf-heading">Two-step verification</h3>
          <p className="tf-text">
            {useBackup
              ? "Enter one of your backup codes."
              : "Open your authenticator app and enter the 6-digit code for CBX Suite."}
          </p>
        </>
      ) : (
        <>
          <h3 className="tf-heading">Set up your authenticator app</h3>
          <p className="tf-text">
            Your bank requires an authenticator app to sign in. Install Google
            Authenticator, Microsoft Authenticator or Authy on your phone, scan
            this code, then enter the 6-digit code it shows.
          </p>
          {setup ? (
            <div className="tf-qr">
              <img src={setup.qrCode} alt="QR code for your authenticator app" />
              <small>
                Can&apos;t scan? Enter this key in the app:
                <code>{setup.secret}</code>
              </small>
            </div>
          ) : (
            <div className="tf-qr tf-qr--loading" />
          )}
        </>
      )}

      <CodeInput
        autoFocus
        value={code}
        onChange={setCode}
        allowBackup={useBackup}
      />

      <Button
        disableElevation
        className="suite-submit-button"
        type="submit"
        disabled={busy || (step === "setup" && !setup)}
      >
        {busy ? "Checking..." : "Verify"}
      </Button>

      <div className="tf-links">
        {step === "verify" && (
          <button
            type="button"
            onClick={() => {
              setUseBackup((value) => !value);
              setCode("");
            }}
          >
            {useBackup ? "Use authenticator app" : "Use a backup code"}
          </button>
        )}
        <button type="button" onClick={onCancel}>
          Back to sign in
        </button>
      </div>
    </form>
  );
}
